import React from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import Link from 'react-router-dom/Link';
import Input from 'react-toolbox/lib/input';
import Card from 'react-toolbox/lib/card';
import { compose, withState, withProps, lifecycle } from 'recompose';
import {
  combineValidators,
  composeValidators,
  isRequired,
  matchesField,
} from 'revalidate';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { renderError as renderFieldError } from '../utils/formUtils';
import { httpPost, httpGet } from '../http';
import CardBody from './CardBody';
import LoadingButton from './LoadingButton';
import PasswordValidationStatus, {
  isValidPassword,
} from './PasswordValidationStatus';
import Spinner from './Spinner';
import styles from './Form.scss';

const texts = {
  info: ['Please choose a new password'],
  error: ['Whoops! Something went wrong. Please try again later.'],
  success: [
    'Your password has been changed!',
    'You can now login using your new password.',
  ],
  placeholderPassword: 'New password',
  placeholderPasswordConfirm: 'Confirm password',
  buttonBack: 'Back to login',
  buttonSubmit: 'Reset my password',
  inputPlaceholder: 'Email',
  missingTokenError: [
    'Token is missing. Please ensure to use the link provided to you in the email',
  ],
  invalidTokenError: [
    'Token is invalid',
    'Token has already been used or has expired',
  ],
};

const validate = combineValidators({
  password: composeValidators(
    isRequired({
      message: 'A new password is required',
    }),
    isValidPassword()
  )(),
  passwordConfirm: composeValidators(
    isRequired({
      message: 'You need to confirm your new password',
    }),
    matchesField('password')({
      message: 'Passwords must match',
    })
  )(),
});

const propTypes = {
  state: PropTypes.shape({
    error: PropTypes.bool,
    isLoading: PropTypes.bool,
    success: PropTypes.bool,
    value: PropTypes.string,
  }),
  setState: PropTypes.func,
  query: PropTypes.shape({
    token: PropTypes.string,
  }),
};

const ResetPasswordForm = props => {
  const { state, setState, query } = props;

  function handleSubmit(e) {
    e.preventDefault();
    setState({
      ...state,
      isLoading: true,
    });

    httpPost(`/api/reset/${query.token}`, {
      ...state.values,
    })
      .then(() => {
        setState({
          ...state,
          isLoading: false,
          error: false,
          success: true,
        });
      })
      .catch(() => {
        setState({
          ...state,
          isLoading: false,
          error: true,
          success: false,
        });
      });
  }

  function handleChange(value, e) {
    setState({
      ...state,
      values: {
        ...state.values,
        [e.target.name]: value,
      },
    });
  }

  function handleFocus() {
    setState({
      ...state,
      meta: {
        focused: true,
      },
    });
  }

  function handleBlur() {
    const { meta, ...newState } = state;
    setState({
      ...newState,
    });
  }

  function renderInfo() {
    return (
      <div className={`mb-2 ${styles.body}`}>
        {texts.info.map(text => <p key={text}>{text}</p>)}
      </div>
    );
  }

  function renderError() {
    return (
      <div className={styles.alertError}>
        {texts.error.map(text => <p key={text}>{text}</p>)}
      </div>
    );
  }

  function renderMissingTokenError() {
    return (
      <div className={styles.alertError}>
        {texts.missingTokenError.map(text => <p key={text}>{text}</p>)}
      </div>
    );
  }

  function renderInvalidTokenError() {
    return (
      <div className={styles.alertError}>
        {texts.invalidTokenError.map(text => <p key={text}>{text}</p>)}
      </div>
    );
  }

  function renderSuccess() {
    return (
      <div className={styles.alertSuccess}>
        {texts.success.map(text => <p key={text}>{text}</p>)}
      </div>
    );
  }

  if (!query.token) return renderMissingTokenError();
  if (state.isLoading) return <Spinner />;

  const passwordIsFocused = _get(state, 'meta.focused', false);
  const passwordValue = _get(state, 'values.password', '');
  const passwordConfirmValue = _get(state, 'values.passwordConfirm', false);
  const passwordIsTouched = passwordValue !== '';
  const passwordConfirmIsTouched = passwordConfirmValue !== false;
  const pristine = !passwordIsTouched && !passwordConfirmIsTouched;
  const errors = validate(state.values);
  const passwordError = _get(errors, 'password', '');
  const passwordConfirmError = _get(errors, 'passwordConfirm', '');
  const isValid = _isEmpty(errors);

  return (
    <div>
      {!state.error &&
        !state.success &&
        !state.invalidTokenError &&
        renderInfo()}
      {state.error && renderError()}
      {state.success && renderSuccess()}
      {state.isLoading && <Spinner />}
      {state.invalidTokenError && renderInvalidTokenError()}
      <form
        id="reset-password-form"
        className={styles.form}
        onSubmit={handleSubmit}
      >
        {!state.isLoading &&
          !state.invalidTokenError &&
          !state.success && (
            <div className={styles.loginFormInput}>
              <Input
                className={styles.loginInput}
                id="password"
                type="password"
                name="password"
                error={renderFieldError(
                  { name: 'password' },
                  passwordError,
                  passwordIsTouched
                )}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                value={passwordValue}
                placeholder={texts.placeholderPassword}
                autoComplete="off"
              />
              <Input
                className={styles.loginInput}
                id="password-confirm"
                type="password"
                name="passwordConfirm"
                onChange={handleChange}
                error={renderFieldError(
                  { name: 'password-confirm' },
                  passwordConfirmError,
                  passwordConfirmIsTouched
                )}
                value={passwordConfirmValue}
                placeholder={texts.placeholderPasswordConfirm}
              />
              {passwordIsFocused && (
                <Card className={styles.flyoutValidation}>
                  <CardBody className={styles.validationStatus}>
                    <h3 className="text-body-2 mb-2">The password must:</h3>
                    <PasswordValidationStatus
                      value={passwordValue}
                      textReplacement={{ pattern: /password must/i, with: '' }}
                    />
                  </CardBody>
                </Card>
              )}
            </div>
          )}
        <div className={styles.footer}>
          {!state.invalidTokenError && !state.success && (
              <LoadingButton
                raised
                accent
                className={styles.footerSubmit}
                disabled={pristine || !isValid}
                type="submit"
                onClick={handleSubmit}
                isLoading={state.isLoading}
                form="reset-password-form"
              >
                {texts.buttonSubmit}
              </LoadingButton>
            )}
          <Link to="/login" className={styles.link}>
            &#8592; {texts.buttonBack}
          </Link>
        </div>
      </form>
    </div>
  );
};

ResetPasswordForm.propTypes = propTypes;

const enhance = compose(
  withState('state', 'setState', {
    error: false,
    value: '',
    isLoading: true,
    success: false,
    invalidTokenError: false,
    values: {
      password: '',
    },
    meta: {
      focused: false,
    },
  }),
  withProps(() => {
    const query = qs.parse(window.location.search.replace('?', ''));
    return {
      query,
    };
  }),
  lifecycle({
    componentWillMount() {
      const { query, setState, state } = this.props;

      // Validate the token before displaying the form
      httpGet(`/api/reset/${query.token}`)
        .then(() => {
          setState({
            ...state,
            isLoading: false,
            invalidTokenError: false,
          });
        })
        .catch(() => {
          setState({
            ...state,
            isLoading: false,
            invalidTokenError: true,
          });
        });
    },
  })
);

export default enhance(ResetPasswordForm);



// WEBPACK FOOTER //
// ./src/client/src/components/ResetPasswordForm.js